import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  effect,
  computed,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RouterLink } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { canViewInstallations } from '@core/helpers/installation-access.helper';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import {
  DeviceChannelConfiguration,
  Installation,
  PvArrayConfig,
  PvForecastingConfig,
  getUserFeaturePermissions,
} from '@core/models';
import { InstallationAttributesService } from '@core/services/installation-attributes.service';
import { FilledButtonComponent } from '@custom-components/general/button/filled-button/filled-button.component';
import { TextButtonComponent } from '@custom-components/general/button/text-button/text-button.component';
import { CardComponent } from '@custom-components/general/card';
import { DetailFieldComponent } from '@custom-components/general/detail-field/detail-field.component';
import { SkeletonBlockComponent } from '@custom-components/general/skeleton/skeleton-block/skeleton-block.component';
import { TagComponent } from '@custom-components/general/tag/tag.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import {
  AttributeRead,
  DeviceAttributeReads,
  InstallationAttributesStore,
} from '@root/app/store/installation-attributes.store';
import { UserStore } from '@root/app/store/user.store';
import { EMPTY, Observable, of } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import {
  PvForecastEmeterChannel,
  PvForecastFormGroup,
  PvForecastFormValue,
  createPvForecastForm,
  isSamePvForecast,
  watchPvForecastForm,
  wattPeakToKilowattPeak,
} from '../../../devices/components/pv-forecast-form/pv-forecast-form';
import { PvForecastFormComponent } from '../../../devices/components/pv-forecast-form/pv-forecast-form.component';
import {
  AZIMUTH_PRESETS,
  PresetOption,
  TILT_PRESETS,
  presetLabelKeyFor,
} from '../../../devices/components/pv-forecast-presets';
import { otherInstallations } from '../../device-attributes';
import { FedDevice, deviceSelection } from '../../fed-devices';
import { pvEmeterChannels } from '../../pv-emeter-channels';
import { settingsRefusal } from '../../settings-refusal';
import { InstallationDeviceSelectComponent } from '../installation-device-select/installation-device-select.component';
import {
  SharedDeviceDialogComponent,
  SharedDeviceDialogData,
} from '../shared-device-dialog/shared-device-dialog.component';

const LABELS = 'installations.attributes';

export interface PvArrayRow {
  tilt: string;
  orientation: string;
  kwp: number;
}

export interface PvBlock {
  deviceId: number;
  ldn: string;
  // Set when the config could not be read; the block then shows only this.
  message: string | null;
  // False for a device that has no forecast config yet, which a user who may set one up still sees.
  configured: boolean;
  channel: string;
  active: boolean;
  arrays: PvArrayRow[];
  totalKwp: number;
  // Whether the user may change the forecast of this device.
  editable: boolean;
  // A forecast only means something for a production meter, so only such a meter may have one
  // switched on. Switching it off is always possible.
  enableBlocked: boolean;
  // Set when the meter is known not to be a production meter, which the block then explains.
  productionOnly: boolean;
  // The device must be read before its forecast can be edited, since its protocol decides the channels.
  ready: boolean;
  deviceLoading: boolean;
  // Whether the forecast is edited in the card: whoever may edit it sees a form for every device
  // that has a forecast, and for one that has none and may have one switched on.
  inPlace: boolean;
  busy: boolean;
  // Why the last change was refused, as a translation key.
  errorKey: string | null;
}

interface EditorSource {
  deviceId: number;
  // What the device has now, or null when it has no forecast config.
  baseline: Pick<PvForecastFormValue, 'device_index' | 'pv_arrays'> | null;
  channels: PvForecastEmeterChannel[];
}

interface PvEditor extends EditorSource {
  form: PvForecastFormGroup;
  value: Signal<PvForecastFormValue | null>;
}

// The PV forecast config of the devices feeding the installation, one device at a time, read from
// the device where prd keeps it. A device without a forecast config adds nothing to a user who
// cannot set one up, so with none the card is left out.
@Component({
  selector: 'xemex-installation-pv-forecast-card',
  imports: [
    CardComponent,
    DetailFieldComponent,
    FilledButtonComponent,
    InstallationDeviceSelectComponent,
    MatSlideToggleModule,
    NgTemplateOutlet,
    PvForecastFormComponent,
    RouterLink,
    SkeletonBlockComponent,
    TagComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-pv-forecast-card.component.html',
})
export class InstallationPvForecastCardComponent {
  installation = input.required<Installation>();
  devices = input.required<FedDevice[]>();

  private readonly store = inject(InstallationAttributesStore);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly attributes = inject(InstallationAttributesService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly APP_PATHS = APP_PATHS;

  // The forecast config is edited through the device's own routes, so whoever may read devices.
  // Nothing is edited from a Retired installation, not even the devices it was fed by.
  private readonly mayEdit = computed(() => {
    const user = this.userStore.user();
    const permissions = this.authStore.permissions();
    return (
      this.installation().status === 'Active' &&
      canViewInstallations(user, permissions) &&
      getUserFeaturePermissions(user, permissions, XEMEX_OID).includes('view_device')
    );
  });

  // The device a forecast change is under way for: the user confirming or the write in flight. One
  // change at a time.
  private readonly busyDevice = signal<number | null>(null);
  protected readonly saving = signal(false);
  private readonly errors = signal<Record<number, string>>({});

  readonly pvBlocks = computed<PvBlock[]>(() =>
    this.devices().flatMap(device => {
      const reads = this.readsOf(device.deviceId);
      const read = reads.pvConfig;
      if (read.state === 'loading') return [];

      const resolved = this.store.meterRoleOf(device.deviceId);
      const config = read.state === 'loaded' ? read.value : null;
      const editable = this.mayEdit();
      const enableBlocked = !(resolved.state === 'resolved' && resolved.role === 'production');
      const ready = reads.device.state === 'loaded';
      const block: PvBlock = {
        deviceId: device.deviceId,
        ldn: device.ldn,
        message: null,
        configured: false,
        channel: '',
        active: false,
        arrays: [],
        totalKwp: 0,
        editable,
        enableBlocked,
        productionOnly: resolved.state === 'resolved' && resolved.role !== 'production',
        ready,
        deviceLoading: reads.device.state === 'loading',
        inPlace: false,
        busy: this.busyDevice() !== null,
        errorKey: this.errors()[device.deviceId] ?? null,
      };
      if (read.state !== 'loaded') {
        return [{ ...block, message: this.translate.instant(`${LABELS}.${read.state}`) }];
      }

      if (!config) {
        return editable ? [{ ...block, inPlace: ready && !enableBlocked }] : [];
      }
      return [
        {
          ...block,
          configured: true,
          inPlace: editable && ready,
          channel: `${config.device_index}_${config.device_type}`,
          active: config.active,
          arrays: config.pv_arrays.map(array => this.pvArrayRow(array)),
          totalKwp: wattPeakToKilowattPeak(
            config.pv_arrays.reduce((total, array) => total + array.wp, 0),
          ),
        },
      ];
    }),
  );

  constructor() {
    effect(() => {
      const deviceId = this.editedDeviceId();
      if (deviceId !== undefined && !untracked(this.channelReads)[deviceId]) {
        this.readChannels(deviceId);
      }
    });
  }

  readonly selectedDeviceId = deviceSelection(this.devices);
  readonly selectedBlock = computed(
    () => this.pvBlocks().find(block => block.deviceId === this.selectedDeviceId()) ?? null,
  );
  // The blocks leave out a device whose forecast config is still being read.
  protected readonly selectedLoading = computed(() => {
    const deviceId = this.selectedDeviceId();
    return deviceId !== null && this.readsOf(deviceId).pvConfig.state === 'loading';
  });

  private readonly editedBlock = computed(() => {
    const block = this.selectedBlock();
    return block?.inPlace ? block : null;
  });

  // The energy meter is picked from the channels of the device, which are read once its forecast is
  // edited in the card.
  private readonly channelReads = signal<Record<number, AttributeRead<DeviceChannelConfiguration>>>(
    {},
  );
  private readonly editedDeviceId = computed(() => this.editedBlock()?.deviceId);
  protected readonly channelsFailed = computed(() => {
    const deviceId = this.editedDeviceId();
    return deviceId !== undefined && this.channelReads()[deviceId]?.state === 'failed';
  });

  // What the form is built from. Whether the forecast is active is no part of it, so switching it
  // does not discard what the user typed, and only a change to what the form sets builds the form
  // again, which then shows what was saved.
  private readonly editorSource = computed<EditorSource | null>(
    () => {
      const block = this.editedBlock();
      const channels = block ? this.channelReads()[block.deviceId] : undefined;
      if (!block || channels?.state !== 'loaded') return null;

      const config = this.configOf(block.deviceId);
      const device = this.readsOf(block.deviceId).device;
      return {
        deviceId: block.deviceId,
        baseline: config
          ? { device_index: config.device_index, pv_arrays: config.pv_arrays }
          : null,
        channels: pvEmeterChannels(
          channels.value,
          device.state === 'loaded' ? device.value.protocol : undefined,
        ),
      };
    },
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );

  protected readonly editor = computed<PvEditor | null>(() => {
    const source = this.editorSource();
    if (!source) return null;

    const form = createPvForecastForm(source.baseline, source.channels);
    return { ...source, form, value: watchPvForecastForm(form) };
  });

  protected readonly canSave = computed(() => {
    const editor = this.editor();
    const value = editor?.value();
    if (!editor || !value || this.busyDevice() !== null) return false;
    return !editor.baseline || !isSamePvForecast(value, editor.baseline);
  });

  protected retryChannels(): void {
    const deviceId = this.editedDeviceId();
    if (deviceId !== undefined) this.readChannels(deviceId);
  }

  private readChannels(deviceId: number): void {
    this.channelReads.update(reads => ({ ...reads, [deviceId]: { state: 'loading' } }));
    this.attributes
      .channelConfiguration(deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: value =>
          this.channelReads.update(reads => ({ ...reads, [deviceId]: { state: 'loaded', value } })),
        error: () =>
          this.channelReads.update(reads => ({ ...reads, [deviceId]: { state: 'failed' } })),
      });
  }

  // A device that has no forecast gets one, switched on, and a device that has one keeps whether it
  // is active.
  protected save(): void {
    const editor = this.editor();
    const value = editor?.value();
    if (!editor || !value || this.busyDevice() !== null) return;

    const { deviceId } = editor;
    this.busyDevice.set(deviceId);
    this.saving.set(true);
    this.clearError(deviceId);
    this.confirmSharedDevice(deviceId)
      .pipe(
        switchMap(confirmed =>
          confirmed
            ? this.attributes.savePvConfig(deviceId, this.configOf(deviceId), value)
            : EMPTY,
        ),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.busyDevice.set(null);
          this.saving.set(false);
        }),
      )
      .subscribe({
        next: saved => {
          this.store.setPvConfig(deviceId, saved);
          this.snackBar.open(this.translate.instant('devices.pvForecast.saved'), '', {
            horizontalPosition: 'right',
            verticalPosition: 'top',
            duration: 3000,
          });
        },
        error: (error: unknown) => {
          const { messageKey, stale } = settingsRefusal(toRefusal(error));
          this.errors.update(errors => ({ ...errors, [deviceId]: messageKey }));
          if (stale) this.store.reloadDevice(deviceId);
        },
      });
  }

  protected toggle(deviceId: number, event: MatSlideToggleChange): void {
    const config = this.configOf(deviceId);
    const restore = (): void => {
      event.source.checked = config?.active ?? false;
    };
    if (!config || this.busyDevice() !== null) {
      restore();
      return;
    }

    this.busyDevice.set(deviceId);
    this.clearError(deviceId);
    this.confirmSharedDevice(deviceId)
      .pipe(
        switchMap(confirmed => {
          if (confirmed) return this.attributes.setPvActive(deviceId, config, event.checked);
          restore();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.busyDevice.set(null)),
      )
      .subscribe({
        next: () => this.store.reloadDevice(deviceId),
        error: (error: unknown) => {
          restore();
          const { messageKey, stale } = settingsRefusal(toRefusal(error));
          this.errors.update(errors => ({ ...errors, [deviceId]: messageKey }));
          if (stale) this.store.reloadDevice(deviceId);
        },
      });
  }

  // A device that feeds other installations changes on all of them, which the form cannot say, so
  // the user is asked first, also when the count could not be read. Emits true to go on.
  private confirmSharedDevice(deviceId: number): Observable<boolean> {
    const others = otherInstallations(
      this.readsOf(deviceId).channelUsage,
      this.installation().installationOid,
    );
    if (others === 0) return of(true);
    return this.dialog
      .open<SharedDeviceDialogComponent, SharedDeviceDialogData, true>(
        SharedDeviceDialogComponent,
        {
          panelClass: 'no-padding-dialog',
          width: '32rem',
          maxWidth: 'calc(100vw - 2rem)',
          data: {
            ldn:
              this.devices().find(device => device.deviceId === deviceId)?.ldn ?? String(deviceId),
            otherInstallations: others,
          },
        },
      )
      .afterClosed()
      .pipe(map(confirmed => confirmed === true));
  }

  private configOf(deviceId: number): PvForecastingConfig | null {
    const read = this.readsOf(deviceId).pvConfig;
    return read.state === 'loaded' ? read.value : null;
  }

  private clearError(deviceId: number): void {
    this.errors.update(errors => {
      const rest = { ...errors };
      delete rest[deviceId];
      return rest;
    });
  }

  private readsOf(deviceId: number): DeviceAttributeReads {
    return (
      this.store.devices()[deviceId] ?? {
        device: { state: 'loading' },
        phaseCount: { state: 'loading' },
        pvConfig: { state: 'loading' },
        channelUsage: { state: 'loading' },
      }
    );
  }

  private pvArrayRow(array: PvArrayConfig): PvArrayRow {
    return {
      tilt: this.presetDisplay(TILT_PRESETS, array.tilt),
      orientation: this.presetDisplay(AZIMUTH_PRESETS, array.azimuth),
      kwp: wattPeakToKilowattPeak(array.wp),
    };
  }

  private presetDisplay(presets: PresetOption[], value: number): string {
    const labelKey = presetLabelKeyFor(presets, value);
    return labelKey ? this.translate.instant(labelKey) : `${value}°`;
  }
}
